import { PureComponent, createRef } from 'react';

// Authentication
import { withAuth } from 'services/auth';

// Notifications provider
import { withSnackbar } from 'notistack';

// Router provider
import { withRouter } from 'react-router-dom';

// To handle error messages
import { getErrorMessage } from 'helpers';

// Firebase
import firebase from 'firebase/app';
import 'firebase/firestore';

// Material UI components / helpers
import { Container, withStyles, Grid } from '@material-ui/core';

// Import the booking dialog
import BookingDialog from 'components/BookingDialog';

// Import the group session booking dialog
import GroupSessionBookingDialog from 'components/GroupSessionBookingDialog';

// Export cleanup
import compose from 'recompose/compose';

// Async status
import { AsyncStatus, TabContainer } from 'components';

// Booking card
import {
  DesiredBooking,
  SearchToolbar,
  TutorBookingCard,
  GroupSessionTutorBookingCard
} from './components';

// Component styles
import styles from './styles';

import moment from 'moment';
import { DateTime } from 'luxon';

// Prototype function for objects
function isEmpty(obj) {
  if (obj == null) return false;
  return Object.entries(obj).length === 0;
}

class Search extends PureComponent {
  constructor(props) {
    super(props);

    // Set default date range for search
    const defaultRange = {
      min: DateTime.fromObject({ hour: 0 }),
      max: DateTime.fromObject({ hour: 0 }).plus({ days: 6 })
    };

    // Initialzie the state
    this.state = {
      hasSearched: false,
      loading: false,
      loadingGroupSearch: false,
      message: null,
      groupSessionMessage: null,
      error: null,
      groupSessionError: null,
      allClasses: {},
      tabIndex: 0,
      student: null,
      allGroupSessions: {},
      selectedBooking: null,
      dialogOpen: false,
      searchToolbarTab: props.initialSearchType === 'group' ? 1 : 0,
      // Persist search filters across tab switches
      searchFilters: {
        start: defaultRange.min,
        end: defaultRange.min.plus({ hours: 24 }).minus({ seconds: 1 }),
        selectedTime: 'anytime'
      }
    };

    // Initialize the ready flag and bind any functions
    this.ready = true;

    // Create a reference to the booking dialog
    this.bookingDialog = createRef();

    // Bind state-manipulating functions
    this.onSearch = this.onSearch.bind(this);
    this.onGroupSearch = this.onGroupSearch.bind(this);
    this.confirmBooking = this.confirmBooking.bind(this);
    this.confirmGroupSessionBooking =
      this.confirmGroupSessionBooking.bind(this);
    this.checkTimeConflict = this.checkTimeConflict.bind(this);
    this.onSearchFiltersChange = this.onSearchFiltersChange.bind(this);
  }

  componentWillUnmount() {
    this.ready = false;
  }

  async componentDidMount() {
    const { auth } = this.props;
    const studentProfileData = await auth.api.profile.getStudentProfile(
      auth.user?.uid
    );
    if (studentProfileData) this.setState({ student: studentProfileData });
  }

  onSearchFiltersChange(filters) {
    // Update search filters in parent state to persist across tab switches
    this.setState({
      searchFilters: {
        ...this.state.searchFilters,
        ...filters
      }
    });
  }

  async onSearch(query) {
    // Update the state to start loading
    this.setState({
      loading: true,
      hasSearched: true
    });

    try {
      // Setup references to the API endpoints
      const { auth } = this.props;
      const { student } = this.state;

      // Retrive the current user's bookings from the API.
      const data = await auth.api.bookings.searchBookings(query);

      // Update the component's state with the bookings.
      if (this.ready) {
        const apiMessage = data?.message || null;
        let classesParsed = {};
        let classesParsedMock = {};
        let sortedDates = [];
        let sortedDatesMock = [];
        const isMockStudent = student?.profile?.status === 'mock';
        let totalResults = 0;
        let trainingResults = 0;
        data &&
          Object.keys(data.search).map((key) => {
            let tutorClasses = data.search[key];
            tutorClasses.map((item) => {
              totalResults += 1;
              if (item?.profile?.tutor?.status === 'training') {
                trainingResults += 1;
              }
              if (
                isMockStudent &&
                item?.profile?.tutor?.status === 'training'
              ) {
                let time = moment(item.when).format('YYYYMMDD');
                if (
                  !Object.prototype.hasOwnProperty.call(
                    classesParsedMock,
                    `${time}`
                  )
                ) {
                  sortedDatesMock.push({
                    momentDate: time,
                    dateFormattedKey: time
                  });
                  classesParsedMock[`${time}`] = {
                    [key]: [
                      {
                        id: item.id,
                        info: item.info,
                        payment: item.payment,
                        profile: {
                          ...item.profile,
                          tutor: { ...item.profile.tutor, isPte: true }
                        },
                        status: item.status,
                        student: item.student,
                        tutor: item.tutor,
                        when: item.when
                      }
                    ]
                  };
                } else if (
                  !Object.prototype.hasOwnProperty.call(
                    classesParsedMock[`${time}`],
                    `${key}`
                  )
                ) {
                  classesParsedMock[`${time}`] = {
                    ...classesParsedMock[`${time}`],
                    [key]: [
                      {
                        id: item.id,
                        info: item.info,
                        payment: item.payment,
                        profile: {
                          ...item.profile,
                          tutor: { ...item.profile.tutor, isPte: true }
                        },
                        status: item.status,
                        student: item.student,
                        tutor: item.tutor,
                        when: item.when
                      }
                    ]
                  };
                } else {
                  classesParsedMock[`${time}`][`${key}`].push({
                    id: item.id,
                    info: item.info,
                    payment: item.payment,
                    profile: {
                      ...item.profile,
                      tutor: { ...item.profile.tutor, isPte: true }
                    },
                    status: item.status,
                    student: item.student,
                    tutor: item.tutor,
                    when: item.when
                  });
                }
              } else if (item?.profile?.tutor?.status !== 'training') {
                let time = moment(item.when).format('YYYYMMDD');
                if (
                  !Object.prototype.hasOwnProperty.call(
                    classesParsed,
                    `${time}`
                  )
                ) {
                  sortedDates.push({
                    momentDate: time,
                    dateFormattedKey: time
                  });
                  classesParsed[`${time}`] = {
                    [key]: [
                      {
                        id: item.id,
                        info: item.info,
                        payment: item.payment,
                        profile: item.profile,
                        status: item.status,
                        student: item.student,
                        tutor: item.tutor,
                        when: item.when
                      }
                    ]
                  };
                } else if (
                  !Object.prototype.hasOwnProperty.call(
                    classesParsed[`${time}`],
                    `${key}`
                  )
                ) {
                  classesParsed[`${time}`] = {
                    ...classesParsed[`${time}`],
                    [key]: [
                      {
                        id: item.id,
                        info: item.info,
                        payment: item.payment,
                        profile: item.profile,
                        status: item.status,
                        student: item.student,
                        tutor: item.tutor,
                        when: item.when
                      }
                    ]
                  };
                } else {
                  classesParsed[`${time}`][`${key}`].push({
                    id: item.id,
                    info: item.info,
                    payment: item.payment,
                    profile: item.profile,
                    status: item.status,
                    student: item.student,
                    tutor: item.tutor,
                    when: item.when
                  });
                }
              }
            });
          });
        const finalClasses = isMockStudent ? classesParsedMock : classesParsed;

        let messageToShow = apiMessage;
        if (
          !isMockStudent &&
          totalResults > 0 &&
          trainingResults === totalResults
        ) {
          messageToShow =
            'Lessons were found, but they are from tutors in training and are hidden for regular students.';
        } else if (!isMockStudent && trainingResults > 0 && !apiMessage) {
          messageToShow =
            'Some lessons are hidden because the tutor is in training.';
        }

        this.setState({
          message: messageToShow,
          loading: false,
          allClasses: finalClasses
        });
      }
    } catch (error) {
      // Update the component's state to show an error
      if (this.ready) {
        this.setState({
          loading: false,
          error: getErrorMessage(error)
        });
      }

      // Log the error to the console.
      console.error(error);
    }
  }

  async onGroupSearch(query) {
    // Update the state to start loading
    this.setState({
      loadingGroupSearch: true,
      hasSearched: true
    });

    try {
      // Setup references to the API endpoints
      const { auth } = this.props;
      const { student } = this.state;

      // Retrive the current user's bookings from the API.
      console.log('DEBUG FE: Querying group sessions with:', query);
      const data = await auth.api.groupSessions.searchGroupSessions(query);
      console.log('DEBUG FE: Data received from API:', data);

      // if (data && data.search && typeof data.search === 'object') {
      //   const groupedData = Object.entries(data.search).reduce(
      //     (acc, [uid, bookings]) => {
      //       if (!acc[uid]) {
      //         acc[uid] = {};
      //       }

      //       bookings.forEach((booking) => {
      //         const groupSessionDate = moment(booking.sessionTime).format(
      //           'YYYYMMDD'
      //         );

      //         if (!acc[uid][groupSessionDate]) {
      //           acc[uid][groupSessionDate] = [];
      //         }

      //         acc[uid][groupSessionDate].push(booking);
      //       });

      //       return acc;
      //     },
      //     {}
      //   );
      // }

      if (this.ready) {
        this.setState({ groupSessionError: null });

        let classesParsed = {};
        let classesParsedMock = {};
        let sortedDates = [];
        let sortedDatesMock = [];

        const isMockStudent = student?.profile?.status === 'mock';
        const now = moment();
        const apiMessage = data?.message || null;
        let totalSessions = 0;
        let trainingSessions = 0;

        if (data && data.search) {
          Object.keys(data.search).forEach((groupSessionId) => {
            const tutorClasses = data.search[groupSessionId];
            if (!Array.isArray(tutorClasses) || tutorClasses.length === 0)
              return;
            totalSessions += 1;
            if (tutorClasses[0]?.tutor?.status === 'training') {
              trainingSessions += 1;
            }

            // Sort all lessons of this group by their date/time
            const sortedLessons = tutorClasses.sort(
              (a, b) => moment(a.when).valueOf() - moment(b.when).valueOf()
            );

            // Filter all upcoming lessons (>= today)
            const upcomingLessons = sortedLessons.filter((lesson) =>
              moment(lesson.when).isSameOrAfter(now, 'day')
            );

            // If none upcoming, show under last lesson’s date
            const targetLessons = upcomingLessons.length
              ? upcomingLessons
              : [sortedLessons[sortedLessons.length - 1]];

            // Use the correct data structures based on mock status
            const targetClasses =
              isMockStudent && tutorClasses[0]?.tutor?.status === 'training'
                ? classesParsedMock
                : classesParsed;
            const targetSortedDates =
              isMockStudent && tutorClasses[0]?.tutor?.status === 'training'
                ? sortedDatesMock
                : sortedDates;

            // ✅ Loop through all upcoming lesson dates
            targetLessons.forEach((lesson) => {
              const lessonDateKey = moment(lesson.when).format('YYYYMMDD');

              // Initialize if this date doesn’t exist
              if (!targetClasses[lessonDateKey]) {
                targetSortedDates.push({
                  momentDate: lessonDateKey,
                  dateFormattedKey: lessonDateKey
                });
                targetClasses[lessonDateKey] = {};
              }

              // ✅ Store the entire session under every upcoming date
              targetClasses[lessonDateKey][groupSessionId] = sortedLessons.map(
                (item) => ({
                  id: item.id,
                  groupSessionId: item.groupSessionId,
                  lessonCount: item.lessonCount,
                  info: item.info,
                  payment: item.payment,
                  status: item.status,
                  student: item.student,
                  tutor: item.tutor,
                  when: item.when
                })
              );
            });
          });
        }

        // ✅ Sort all dates ascending
        sortedDates.sort((a, b) => a.momentDate.localeCompare(b.momentDate));
        sortedDatesMock.sort((a, b) =>
          a.momentDate.localeCompare(b.momentDate)
        );

        const finalGroupSessions = isMockStudent
          ? classesParsedMock
          : classesParsed;
        const finalSortedDates = isMockStudent ? sortedDatesMock : sortedDates;

        console.log('DEBUG FE: finalGroupSessions:', finalGroupSessions);
        console.log('DEBUG FE: finalSortedDates:', finalSortedDates);

        let messageToShow = apiMessage;
        if (
          !isMockStudent &&
          totalSessions > 0 &&
          trainingSessions === totalSessions
        ) {
          messageToShow =
            'Group sessions were found, but they are from tutors in training and are hidden for regular students.';
        } else if (!isMockStudent && trainingSessions > 0 && !apiMessage) {
          messageToShow =
            'Some group sessions are hidden because the tutor is in training.';
        }

        this.setState({
          groupSessionMessage: messageToShow,
          loadingGroupSearch: false,
          allGroupSessions: finalGroupSessions,
          sortedDates: finalSortedDates
        });
      }
    } catch (error) {
      if (this.ready) {
        this.setState({
          loadingGroupSearch: false,
          groupSessionError: getErrorMessage(error)
        });
      }
      console.error(error);
    }
  }

  // Helper function to check for time conflicts
  async checkTimeConflict(bookingTime) {
    const { auth } = this.props;
    const studentId = auth.api.credentials?.uid;

    if (!studentId || !bookingTime) return false;

    // Check for time conflicts (50 minute window)
    const isTimeConflict = (existingTime, newTime, windowMinutes = 50) => {
      const existing = new Date(existingTime);
      const newBooking = new Date(newTime);
      const diffMinutes = Math.abs(
        (existing.getTime() - newBooking.getTime()) / (1000 * 60)
      );
      return diffMinutes < windowMinutes;
    };

    // Check regular bookings
    const nowMinus1Hour = Date.now() - 60 * 60 * 1000;
    const bookingsSnapshot = await firebase
      .firestore()
      .collectionGroup('bookings')
      .where('student', '==', studentId)
      .where('status', 'in', ['booked', 'pending'])
      .where('when', '>=', nowMinus1Hour)
      .get();

    const bookingConflict = bookingsSnapshot.docs.some((doc) => {
      const booking = doc.data();
      const { when } = booking;
      if (!when) return false;
      return isTimeConflict(when, bookingTime);
    });

    if (bookingConflict) return true;

    // Check group sessions
    const groupSessionsSnapshot = await firebase
      .firestore()
      .collection('groupsessions')
      .get();

    const groupConflict = groupSessionsSnapshot.docs.some((doc) => {
      const sessionData = doc.data();
      const { bookings = [] } = sessionData;

      return bookings.some((booking) => {
        const { studentIds = [], students = [], when } = booking;

        const isStudentInBooking =
          studentIds.includes(studentId) ||
          students.some((student) => {
            return (
              (typeof student === 'object' && student.id === studentId) ||
              student === studentId
            );
          });

        if (isStudentInBooking && when) {
          return isTimeConflict(when, bookingTime);
        }
        return false;
      });
    });

    return groupConflict;
  }

  async confirmBooking(data) {
    // Close the booking dialog
    const { allClasses } = this.state;
    const { booking, values } = data;
    const { enqueueSnackbar, onUpdateTab } = this.props;

    // console.log(search);
    // return;

    // Wrap in a try-catch to handle any errors
    // eslint-disable-next-line no-unreachable
    try {
      // Check for time conflict before booking
      const hasConflict = await this.checkTimeConflict(booking.when);
      if (hasConflict) {
        enqueueSnackbar(
          'You already have a lesson or group session booked at this time. Please choose a different time.',
          { variant: 'error' }
        );
        return;
      }

      // Notify the user that we're attempting to book the slot.
      const { auth } = this.props;
      const { student } = this.state;
      enqueueSnackbar(
        `Booking ${student.profile?.status === 'mock' ? 'Demo' : ''} slot...`
      );
      // set the mock check
      values.is_mock = student.profile?.status === 'mock' ? true : false;

      // Book the slot.
      await auth.api.bookings.bookBooking(booking.tutor, booking.id, {
        ...values,
        tutor: booking?.profile?.tutor
      });

      // Remove the booking from the schedule search
      let updatedAllClasses = {
        ...allClasses,
        [moment(booking.when).format('YYYYMMDD')]: {
          [booking.tutor]: allClasses[moment(booking.when).format('YYYYMMDD')][
            booking.tutor
          ].filter((item) => item.id !== booking.id)
        }
      };

      updatedAllClasses = Object.fromEntries(
        Object.entries(updatedAllClasses).map(([key, data]) => {
          let tutors = data;
          tutors &&
            Object.entries(tutors).map(([key, data]) => {
              if (!data.length) {
                delete tutors[key];
              }
              return tutors;
            });
          return [key, tutors];
        })
      );
      this.setState({
        allClasses: updatedAllClasses
      });

      // Show a success notification
      enqueueSnackbar(
        `${student.profile?.status === 'mock' ? 'Demo' : ''} Slot booked!`,
        { variant: 'success' }
      );

      // Redirect the user to the bookings page
      onUpdateTab(1);
    } catch (error) {
      // Log the error to the console
      console.error(error);

      // Show an error snackbar
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  }

  async confirmGroupSessionBooking(booking) {
    const { enqueueSnackbar, onUpdateTab } = this.props;

    // Wrap in a try-catch to handle any errors
    // eslint-disable-next-line no-unreachable
    try {
      // Get booking times from selected lessons
      const { auth } = this.props;
      const groupSessionId = booking.groupSessionId;
      const selectedLessons = booking?.selectedLessons || [];

      // Fetch group session to get booking times
      const groupSessionRef = firebase
        .firestore()
        .collection('groupsessions')
        .doc(groupSessionId);
      const groupSessionDoc = await groupSessionRef.get();

      if (groupSessionDoc.exists) {
        const sessionData = groupSessionDoc.data();
        const bookings = sessionData.bookings || [];
        const selectedBookings = bookings.filter((b) =>
          selectedLessons.includes(b.bookingId)
        );
        const selectedTimes = selectedBookings.map((b) => b.when);

        // Check for time conflicts for each selected booking time
        for (const bookingTime of selectedTimes) {
          const hasConflict = await this.checkTimeConflict(bookingTime);
          if (hasConflict) {
            enqueueSnackbar(
              'You already have a lesson or group session booked at this time. Please choose a different time.',
              { variant: 'error' }
            );
            return;
          }
        }
      }

      // Notify the user that we're attempting to book the slot.
      const { student } = this.state;
      enqueueSnackbar(
        `Booking ${student.profile?.status === 'mock' ? 'Demo' : ''} slot...`
      );
      console.log('before save', booking);
      // Book the slot.
      await auth.api.groupSessions.bookGroupSessionBooking(
        booking.groupSessionId,
        booking?.selectedLessons
      );

      // Show a success notification
      enqueueSnackbar(
        `${student.profile?.status === 'mock' ? 'Demo' : ''} Slot booked!`,
        { variant: 'success' }
      );

      // Redirect the user to the bookings page
      onUpdateTab(1);
    } catch (error) {
      // Log the error to the console
      console.error(error);

      // Show an error snackbar
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  }

  renderSearch() {
    const { loading, error, allClasses, tabIndex, message } = this.state;

    // Convert allClasses to tabs only if it has valid data
    const filteredClasses = Object.entries(allClasses).filter(
      ([data]) => Object.keys(data).length > 0
    );

    const tabs = filteredClasses.map(([key, data]) => ({
      label: moment(key, 'YYYYMMDD').format('DD MMM YYYY'),
      component: (
        <>
          {Object.entries(data).map(([bookingKey, bookingData]) => (
            <Grid
              key={bookingKey}
              item
              xs={12}
              sm={6}
              md={4}
              lg={4}
              xl={3}
              style={{ padding: '0 9px', marginBottom: 18 }}>
              <TutorBookingCard
                key={bookingKey}
                bookings={bookingData}
                tutor={bookingKey}
                student={this.state.student}
                onBook={(booking) => {
                  this.setState({ selectedBooking: booking });
                  // Open the booking dialog
                  this.bookingDialog.current.open(booking.id, booking);
                }}
              />
            </Grid>
          ))}
        </>
      )
    }));

    // Ensure tabIndex is within valid range
    const validTabIndex = Math.max(0, Math.min(tabIndex, tabs.length - 1));

    if (loading) {
      return <AsyncStatus loading />;
    }
    if (error !== null) {
      // Display the error message and retry button
      return <AsyncStatus error={error} onRetry={this.retryAPICall} retry />;
    }

    // Check if there are no lessons after filtering
    if (this.state.hasSearched && (isEmpty(allClasses) || tabs.length === 0)) {
      return (
        <AsyncStatus
          error={message || 'No lessons that match your criteria were found'}
          textVariant="body1"
        />
      );
    }

    return (
      <>
        {tabs.length ? (
          <TabContainer
            tabs={tabs}
            index={validTabIndex}
            view={'row'}
            onChange={(newIndex) => this.setState({ tabIndex: newIndex })}
          />
        ) : null}
      </>
    );
  }

  renderGroupSearch() {
    const { auth } = this.props;
    const {
      loadingGroupSearch,
      groupSessionError,
      allGroupSessions,
      tabIndex,
      groupSessionMessage
    } = this.state;

    // Filter and structure classes by date for tabs
    const filteredClasses = Object.entries(allGroupSessions).filter(
      ([data]) => Object.keys(data).length > 0
    );

    const tabs = filteredClasses.map(([groupSessionDate, tutorData]) => ({
      label: moment(groupSessionDate, 'YYYYMMDD').format('DD MMM YYYY'),
      component: (
        <>
          {Object.entries(tutorData).map(([tutorId, bookings]) => (
            <Grid
              key={tutorId}
              item
              xs={12}
              sm={6}
              md={4}
              lg={4}
              xl={3}
              style={{ padding: '0 9px', marginBottom: 18 }}>
              <GroupSessionTutorBookingCard
                auth={auth}
                bookings={bookings}
                tutor={tutorId}
                onBook={(booking) => {
                  this.setState({ selectedBooking: booking, dialogOpen: true });
                }}
              />
            </Grid>
          ))}
        </>
      )
    }));

    // Ensure tabIndex is within the valid range of available tabs
    const validTabIndex = Math.max(0, Math.min(tabIndex, tabs.length - 1));

    // Render loading, error, or tabs based on the current state
    if (loadingGroupSearch) {
      return <AsyncStatus loading />;
    }
    if (groupSessionError) {
      return (
        <AsyncStatus
          error={groupSessionError}
          onRetry={this.retryAPICall}
          retry
        />
      );
    }
    // Check if there are no lessons after filtering
    if (
      this.state.hasSearched &&
      (isEmpty(allGroupSessions) || tabs.length === 0)
    ) {
      return (
        <AsyncStatus
          error={
            groupSessionMessage ||
            'No lessons that match your criteria were found'
          }
          textVariant="body1"
        />
      );
    }

    return (
      <>
        {tabs.length ? (
          <TabContainer
            tabs={tabs}
            index={validTabIndex}
            view="row"
            onChange={(newIndex) => this.setState({ tabIndex: newIndex })}
          />
        ) : null}
      </>
    );
  }

  render() {
    const { classes, auth } = this.props;
    const {
      loading,
      message,
      student,
      groupSessionMessage,
      loadingGroupSearch,
      selectedBooking,
      dialogOpen
    } = this.state;

    // Check if the user has a current subscription
    const hasSubscription = student?.stripe?.subscription?.currentSubscription;

    return (
      <>
        <Container maxWidth="lg">
          <GroupSessionBookingDialog
            open={dialogOpen}
            onClose={() => this.setState({ dialogOpen: false })}
            booking={selectedBooking}
            onConfirm={this.confirmGroupSessionBooking}
          />
          <BookingDialog
            ref={this.bookingDialog}
            auth={auth}
            student={student}
            selectedBooking={selectedBooking}
            onConfirm={this.confirmBooking}
          />
          <div className={classes.root}>
            <SearchToolbar
              onSearch={this.onSearch}
              onGroupSearch={this.onGroupSearch}
              loading={loading}
              onTabChange={(tabIndex) => {
                // Clear search results but preserve search filters
                this.setState({
                  allClasses: {},
                  allGroupSessions: {},
                  hasSearched: false,
                  searchToolbarTab: tabIndex
                });
              }}
              searchFilters={this.state.searchFilters}
              onSearchFiltersChange={this.onSearchFiltersChange}
              loadingGroupSearch={loadingGroupSearch}
              message={message}
              groupSessionMessage={groupSessionMessage}
              renderOneOnOneSearch={this.renderSearch()}
              renderGroupSearch={this.renderGroupSearch()}
              initialTab={this.state.searchToolbarTab}
            />

            {/* {this.renderSearch()} */}
          </div>
        </Container>

        {hasSubscription && (
          <Container maxWidth="lg">
            <DesiredBooking enqueueSnackbar={this.props.enqueueSnackbar} />
          </Container>
        )}
      </>
    );
  }
}

export default compose(
  withSnackbar,
  withRouter,
  withAuth,
  withStyles(styles)
)(Search);
